<template>
  <div class="phone-login">
    <el-form :model="formData" :rules="rules" ref="ruleForm" label-width="0px" class="ruleForm">
      <!-- 手机号 -->
      <el-form-item prop="phone">
        <div class="phone-input-wrapper">
          <el-select
            v-model="formData.areaCode"
            class="area-code-select"
            :placeholder="$t('title.areaCode') || 'Area Code'"
            filterable
          >
            <el-option
              v-for="item in areaCodeList"
              :key="item.value"
              :label="item.value"
              :value="item.value"
            >
              <span>{{ item.fullLabel }}</span>
            </el-option>
          </el-select>
          <el-input
            v-model.trim="formData.phone"
            :placeholder="$t('title.phone')"
            :maxlength="20"
            @keydown.native.enter="focusSmsCode"
          />
        </div>
      </el-form-item>

      <!-- 验证码 -->
      <el-form-item prop="smsCode">
        <el-input
          ref="smsCodeInput"
          v-model.trim="formData.smsCode"
          :placeholder="$t('title.smsCode')"
          :maxlength="6"
          @keydown.native.enter="focusPassword"
        >
          <el-button
            slot="suffix"
            type="text"
            class="send-btn"
            ref="sendBtn"
            :disabled="counting"
            @click="sendCode"
          >
            {{ counting ? `${countdown}${$t('btn.resend')}` : $t('btn.sendCode') }}
          </el-button>
        </el-input>
      </el-form-item>

      <!-- 密码 -->
      <el-form-item prop="password">
        <el-input
          ref="passwordInput"
          v-model="formData.password"
          :placeholder="$t('title.password')"
          show-password
          :maxlength="20"
          @keydown.native.enter="submitLogin"
        />
      </el-form-item>

      <el-form-item>
        <el-button
          style="width: 100%"
          ref="loginBtn"
          type="primary"
          :loading="loading"
          @click="submitLogin"
        >
          {{ $t('btn.login') }}
        </el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { reactive, ref, toRefs, onBeforeUnmount, computed } from '@vue/composition-api';
import { useHttp } from '@/hooks/request';
import { API_PATH, SMS_COUNTDOWN_SECONDS, CAPTCHA_APP_ID } from '@/constants/auth';
import { AREA_CODE_LIST } from '@/constants/areaCode';
import i18n from '@/lang';

export default {
  name: 'PhoneLogin',
  components: {},
  setup(_, context) {
    const { $route, $message } = context.root;
    const state = reactive({
      formData: {
        areaCode: '+86',
        phone: '',
        smsCode: '',
        password: '',
      },
      rules: {
        phone: [
          { required: true, message: i18n.t('msg.phoneRequired'), trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              const digits = (value || '').replace(/\D/g, '');
              if (digits.length < 5 || digits.length > 15) {
                callback(new Error(i18n.t('msg.phoneLengthInvalid')));
              } else {
                callback();
              }
            },
            trigger: 'blur',
          },
        ],
        smsCode: [
          { required: true, message: i18n.t('msg.smsCodeRequired'), trigger: 'blur' },
          { pattern: /^\d{6}$/, message: i18n.t('msg.smsCodeInvalid'), trigger: 'blur' },
        ],
        password: [{ required: true, message: i18n.t('msg.passwordRequired'), trigger: 'blur' }],
      },
      loading: false,
      counting: false,
      countdown: SMS_COUNTDOWN_SECONDS,
    });

    const ruleForm = ref(null);
    const sendBtn = ref(null);
    const smsCodeInput = ref(null);
    const passwordInput = ref(null);
    const loginBtn = ref(null);
    let countdownTimer = null;

    // 接口 hooks（mock 阶段使用，真实接口就绪后切换 API_PATH.SEND_CODE）
    const { postData: sendSmsCode } = useHttp(API_PATH.SEND_CODE);
    const { postData: toLogin } = useHttp(API_PATH.LOGIN);

    // 区号列表（根据当前语言动态显示）
    const areaCodeList = computed(() => {
      return AREA_CODE_LIST.map(item => ({
        value: item.value,
        label: item.value,
        fullLabel: `${item[i18n.locale] || item.zh} ${item.value}`,
      }));
    });

    // 聚焦方法
    const focusSmsCode = () => smsCodeInput.value.focus();
    const focusPassword = () => passwordInput.value.focus();

    /** 防水墙验证成功回调：调用发送验证码接口 */
    const validCode = async res => {
      if (!res.ticket) return;
      const bsId = $route.query.bs_id;
      if (!bsId) {
        $message.error(i18n.t('msg.sendCodeFailed'));
        state.counting = false;
        return;
      }

      try {
        const params = {
          phone: state.formData.phone,
          areaCode: state.formData.areaCode,
          bsId,
          appId: res.appid,
          randStr: res.randstr,
          ticket: res.ticket,
        };
        const result = await sendSmsCode(params);

        // 如果走到这里，说明接口调用成功（code === 0）
        // useHttp 内部已经处理了 code != 0 的情况（显示错误并 reject）
        // 所以这里直接显示成功即可
        $message.success(i18n.t('msg.sendCodeSuccess'));
        state.counting = true;
        startCountdown();
      } catch (err) {
        // useHttp 已经显示了错误消息，这里不再重复显示
        console.error('[PhoneLogin] sendSmsCode failed:', err);
        state.counting = false;
      }
    };

    /** 弹出腾讯防水墙 */
    const tencentCaptcha = () => {
      const dom = sendBtn.value.$el;
      const captcha = new window.TencentCaptcha(dom, CAPTCHA_APP_ID, validCode);
      captcha.show();
    };

    /** 点击「获取验证码」：先校验手机号，再弹防水墙 */
    const sendCode = () => {
      ruleForm.value.validateField('phone', errMsg => {
        if (errMsg) return;
        tencentCaptcha();
      });
    };

    /** 60s 倒计时 */
    const startCountdown = () => {
      state.countdown = SMS_COUNTDOWN_SECONDS;
      countdownTimer = setInterval(() => {
        state.countdown -= 1;
        if (state.countdown <= 0) {
          clearInterval(countdownTimer);
          countdownTimer = null;
          state.counting = false;
        }
      }, 1000);
    };

    /** 提交登录 */
    const submitLogin = async () => {
      const bsId = $route.query.bs_id;
      const fromUrl = $route.query.redirect_url;
      if (!bsId || !fromUrl) {
        $message.error(i18n.t('msg.loginFailed'));
        return;
      }

      try {
        await ruleForm.value.validate();
      } catch (e) {
        return;
      }
      state.loading = true;
      try {
        const data = await toLogin({
          phone: state.formData.phone,
          areaCode: state.formData.areaCode,
          password: state.formData.password,
          verifyCode: state.formData.smsCode,
          bsId,
          loginType: 'phone',
        });
        state.loading = false;
        if (!data) return;

        const storage = context.root.$storage || window.localStorage;
        storage.setItem(bsId, data.businessToken);
        const hasQuery = (fromUrl?.match(/\?/g) || []).length > 0;
        window.location = hasQuery
          ? `${fromUrl}&token=${data.businessToken}`
          : `${fromUrl}?token=${data.businessToken}`;
      } catch (err) {
        console.error('[PhoneLogin] login failed:', err);
        state.loading = false;
      }
    };

    onBeforeUnmount(() => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
    });

    return {
      ...toRefs(state),
      ruleForm,
      sendBtn,
      smsCodeInput,
      passwordInput,
      loginBtn,
      sendCode,
      submitLogin,
      focusSmsCode,
      focusPassword,
      areaCodeList,
    };
  },
};
</script>

<style lang="scss" scoped>
.phone-login {
  .phone-input-wrapper {
    display: flex;
    align-items: center;

    .area-code-select {
      width: 90px;
      margin-right: 8px;
      flex-shrink: 0;
    }
  }

  .send-btn {
    padding-right: 12px;
    color: var(--color-primary, #409eff);
  }
}
</style>
